import React, { useCallback, useEffect, useState } from "react";
import { MagnifyingGlass, Funnel, CaretDown, Check, CaretRight, Warning } from "@phosphor-icons/react";
import { ACCOUNT_EXAMPLES, ASSET_EXAMPLES, TX_EXAMPLES, HOME_FILTERS, FILTER_TO_MODE, CARDANO_NETWORK } from "@/lib/constants";
import { cn } from "@/lib/format";
import { resolveSearch } from "@/lib/search";
import { detectGovTarget, resolveGovSearch } from "@/lib/governance";
import { ChainTipWidget } from "@/components/widgets/ChainTipWidget";
import { ExternalStatusBadge } from "@/components/widgets/ExternalStatusBadge";
import { LatestBlocks } from "@/components/widgets/LatestBlocks";
import { LatestTransactions } from "@/components/widgets/LatestTransactions";
import { TransactionVolume } from "@/components/widgets/TransactionVolume";
import { TopPools } from "@/components/widgets/TopPools";
import { PoolOverviewPanel } from "@/components/widgets/PoolOverviewPanel";
import { GovDigest } from "@/components/governance/GovDigest";

export const HomePage = ({ onSearch, onGovSearch, amountMode, onPickBlock, onPickTx, onPickPool }) => {
  const [home_query, setHomeQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [hint, setHint] = useState(null);
  const inputRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // close the dropdown on outside click / escape
  useEffect(() => {
    if (!filterOpen) return undefined;
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setFilterOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [filterOpen]);

  const submit = useCallback(() => {
    // Explicit Governance filter → route everything inside the governance
    // explorer (incl. a pool id to its SPO voting page).
    if (filter === "governance") {
      const t = resolveGovSearch(home_query);
      if (t) {
        onGovSearch?.(t.path);
        return;
      }
      setHint({ type: "error", text: "Enter a governance action (gov_action…), DRep (drep…) or pool (pool1…) id." });
      return;
    }
    // Default "All filters" → governance actions / DReps still auto-detect and
    // jump to governance; pools & everything else resolve as chain objects.
    if (filter === "all") {
      const gt = detectGovTarget(home_query);
      if (gt) {
        onGovSearch?.(gt.path);
        return;
      }
    }
    const res = resolveSearch(home_query, filter);
    if (res.error) {
      setHint({ type: "error", text: res.error });
      return;
    }
    if (res.hint) setHint({ type: "info", text: res.hint });
    if (res.mode) {
      onSearch(res.mode, res.value);
    } else {
      setHint({ type: "error", text: res.hint || "Search not available for this filter yet." });
    }
  }, [home_query, filter, onSearch, onGovSearch]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") submit();
  };

  const activeFilter = HOME_FILTERS.find((f) => f.key === filter) || HOME_FILTERS[0];

  return (
    <div data-testid="home-page" className="relative">
      {/* faint cyan halos for ambient glow */}
      <div className="halo-cyan absolute -top-32 left-1/4 w-[500px] h-[500px]" aria-hidden="true" />
      <div className="halo-magenta absolute top-20 right-10 w-[300px] h-[300px]" aria-hidden="true" />

      <div className="relative grid-bg">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-16 sm:pb-24 space-y-10 sm:space-y-14">
          {/* Hero */}
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-primary/70 mono">
              <span className="relative inline-flex h-2 w-2" aria-hidden="true">
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary/60" />
              </span>
              <span>{CARDANO_NETWORK === "mainnet" ? "mainnet" : "preprod testnet"} · powered by ada.api.gridscan.io</span>
            </div>
            <div className="flex justify-center">
              <ExternalStatusBadge />
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight">
              <span className="text-primary">ada.</span><span className="text-[#22d3ee]">explorer</span>
            </h1>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Search any address, stake key, transaction, block, asset or pool on the Cardano {CARDANO_NETWORK === "mainnet" ? "mainnet" : "testnet"}.
            </p>
          </div>

          {/* Search panel — filter dropdown is now embedded inside the input bar */}
          <div className="relative z-50">
            <div className="bg-card/50 backdrop-blur-md border border-primary/20 rounded-2xl p-2 flex flex-wrap items-center gap-2">
              {/* filter dropdown button */}
              <div ref={dropdownRef} className="relative shrink-0">
                <button
                  data-testid="home-filter-button"
                  type="button"
                  onClick={() => setFilterOpen((v) => !v)}
                  className={cn(
                    "h-11 px-3.5 inline-flex items-center gap-2 mono uppercase tracking-[0.2em] text-[11px] border rounded-lg transition-colors",
                    filterOpen
                      ? "bg-primary/15 border-primary/50 text-primary glow-active"
                      : "bg-black/40 border-primary/20 text-muted-foreground hover:text-primary hover:border-primary/40",
                  )}
                  aria-haspopup="listbox"
                  aria-expanded={filterOpen}
                >
                  <Funnel size={13} />
                  <span>{activeFilter.label}</span>
                  <CaretDown
                    size={11}
                    className={cn("transition-transform", filterOpen && "rotate-180")}
                  />
                </button>
                {filterOpen ? (
                  <div
                    data-testid="home-filter-dropdown"
                    role="listbox"
                    className="absolute left-0 top-full mt-2 z-[60] min-w-[220px] bg-card/95 backdrop-blur-md border border-primary/30 rounded-lg shadow-xl shadow-primary/10 py-1.5"
                  >
                    {HOME_FILTERS.map((f) => {
                      const isActive = f.key === filter;
                      const isDisabled = FILTER_TO_MODE[f.key] === null && f.key !== "all";
                      return (
                        <button
                          key={f.key}
                          data-testid={`home-filter-${f.key}`}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          onClick={() => {
                            setFilter(f.key);
                            setFilterOpen(false);
                            setHint(null);
                            inputRef.current?.focus();
                          }}
                          className={cn(
                            "w-full text-left px-3 py-2 text-[11px] mono uppercase tracking-[0.2em] flex items-center justify-between gap-3 transition-colors",
                            isActive
                              ? "bg-primary/15 text-primary"
                              : "text-muted-foreground hover:bg-muted/30 hover:text-foreground",
                            isDisabled && !isActive && "opacity-60",
                          )}
                        >
                          <span className="inline-flex items-center gap-2">
                            {isActive ? <Check size={11} /> : <span className="w-3" />}
                            {f.label}
                          </span>
                          {isDisabled ? (
                            <span className="text-[9px] text-muted-foreground/70">soon</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              {/* divider */}
              <span className="hidden sm:block h-7 w-px bg-primary/15 shrink-0" aria-hidden="true" />

              <div className="flex items-center gap-2 min-w-0 w-full basis-full order-last sm:order-none sm:basis-auto sm:flex-1 sm:w-auto">
                <MagnifyingGlass size={18} className="text-primary/70 shrink-0" />
                <input
                  ref={inputRef}
                  data-testid="home-search-input"
                  value={home_query}
                  onChange={(e) => {
                    setHomeQuery(e.target.value);
                    setHint(null);
                  }}
                  onKeyDown={onKeyDown}
                  placeholder={
                    filter === "all"
                      ? "Paste an address, stake key, tx hash, block, asset, DRep or gov action…"
                      : filter === "governance"
                        ? "Paste a gov_action…, drep… or pool1… id"
                        : `Search ${activeFilter.label.toLowerCase()}…`
                  }
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="none"
                  className="flex-1 min-w-0 bg-transparent outline-none py-3 text-[14px] mono text-foreground placeholder:text-muted-foreground/60"
                />
              </div>
              <button
                data-testid="home-search-submit"
                onClick={submit}
                disabled={!home_query.trim()}
                className="glow-cta px-6 py-3 bg-primary text-primary-foreground font-bold tracking-[0.2em] uppercase text-[11px] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 ml-auto"
              >
                search <CaretRight size={12} />
              </button>
            </div>

            {hint ? (
              <div
                data-testid="home-search-hint"
                className={cn(
                  "mt-2 text-[12px] mono px-3 py-2 border rounded-lg inline-flex items-center gap-2",
                  hint.type === "error"
                    ? "bg-destructive/10 border-destructive/30 text-destructive"
                    : "bg-primary/10 border-primary/30 text-primary",
                )}
              >
                <Warning size={12} />
                {hint.text}
              </div>
            ) : null}
          </div>

          {/* Quick examples */}
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">try one of these</div>
            <div className="flex flex-wrap gap-2">
              {[
                ["latest block", "blocks", "latest"],
                ["stake key", "accounts", ACCOUNT_EXAMPLES[0]],
                ["NFT asset", "assets", ASSET_EXAMPLES[0]],
                ["transaction", "transactions", TX_EXAMPLES[0]],
              ].map(([label, mode, val]) => (
                <button
                  key={label}
                  data-testid={`home-quick-${mode}`}
                  onClick={() => onSearch(mode, val)}
                  className="px-3 py-1.5 text-[11px] mono uppercase tracking-[0.18em] bg-card/40 border border-primary/15 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                >
                  {label}
                </button>
              ))}
              <button
                data-testid="home-quick-governance"
                onClick={() => onGovSearch?.("/governance")}
                className="px-3 py-1.5 text-[11px] mono uppercase tracking-[0.18em] bg-card/40 border border-primary/15 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
              >
                governance
              </button>
            </div>
          </div>

          {/* Chain tip — glowing */}
          <ChainTipWidget />

          {/* Transaction volume — live per-block chart (desktop only) */}
          <div className="hidden lg:block">
            <TransactionVolume />
          </div>

          {/* Latest blocks */}
          <div>
            <LatestBlocks amountMode={amountMode} onPickBlock={onPickBlock} />
          </div>

          {/* Latest transactions */}
          <div>
            <LatestTransactions amountMode={amountMode} onPickTx={onPickTx} />
          </div>

          {/* Top pools + overview (overview panel is desktop-only) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
            <div className="hidden lg:block lg:col-span-1">
              <PoolOverviewPanel />
            </div>
            <div className="lg:col-span-2">
              <TopPools amountMode={amountMode} onPickPool={onPickPool} />
            </div>
          </div>

          {/* Governance digest — desktop only */}
          <div className="hidden lg:block">
            <GovDigest onGovSearch={onGovSearch} />
          </div>
        </div>
      </div>
    </div>
  );
};
